import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import enquire from 'enquire.js';
import Sticky from 'react-stickynode';
import PropTypes from 'prop-types';
import { Icon } from '../Icon';

const mediaWidth = 1551;

const BottomBorder = styled.hr`
  box-sizing: content-box;
  height: 0;
  display: block;
  margin: 0em 0em 0em 0.5em;
  border-top: 1px solid ${p => p.theme.colors.aux.lightGrey};
`;

const Container = styled.div<{ isOpen: boolean }>`
  box-sizing: border-box;
  display: block;
  border-radius: 0em;
  height: 100%;
  width: 17.5em;
  z-index: 1200;
  left: 0em;
  top: 0em;
  bottom: 0em;
  transform: translate(0px, 0px);
  overflow-y: auto;
  background-color: ${p => p.theme.colors.aux.white};
  display: ${props => (props.isOpen ? 'block' : 'none')};
`;

const RightAlign = styled.div`
  display: flex;
  height: 2.5em;
  justify-content: flex-end;
  border-bottom: 0.01em solid ${p => p.theme.colors.aux.blueGrey};
`;

const OpenButton = styled.button<{ isOpen: boolean }>`
  height: 3.92em;
  width: 3.92em;
  padding: 0.42em;
  background: inherit;
  border: none;
  display: ${props => (props.isOpen ? 'none' : 'block')};
  min-height: 2.14em;
  fill: ${p => p.theme.colors.aux.lightBlue};
  outline: none;
  &:hover {
    cursor: pointer;
  }
  &:focus {
    outline: 1px solid ${p => p.theme.colors.aux.lightBlue};
  }
`;

const CloseButton = styled.button`
  margin-top: auto;
  background-color: transparent;
  border: none;
  fill: ${p => p.theme.colors.aux.lightBlue};
  outline: none;
  &:hover {
    cursor: pointer;
  }
  &:focus {
    outline: 1px solid ${p => p.theme.colors.aux.lightBlue};
  }
`;

const StyledSticky = styled(Sticky)`
  &.active {
    flex: auto;
    z-index: 9999;
    width: 17.5em;
  }
`;

const NavWrapper = styled.div`
  height: 100%;
  z-index: 99999;
  border-right: solid ${p => p.theme.colors.aux.blueGrey};
`;

interface SidebarProps {
  /** Expected to be some NavigationPanel components */
  children: React.ReactNode;
  // keep the sidebar open permanently and not have collapsible capability
  // (Dev notes: part of MVP for new design, this will likely go away when collapsed new design is implemented)
  alwaysOpen?: boolean;
}

const Sidebar = (props: SidebarProps) => {
  const { alwaysOpen } = props;
  const [open, setOpen] = useState(alwaysOpen ?? false);

  if (!alwaysOpen) {
    useEffect(() => {
      enquire.register(`(min-width: ${mediaWidth}px)`, {
        match: () => {
          setOpen(true);
        },
        unmatch: () => {
          setOpen(false);
        },
      });
    }, []);
  }

  return (
    <NavWrapper>
      <StyledSticky enabled={open}>
        {!alwaysOpen && (
          <OpenButton
            isOpen={open}
            onClick={() => setOpen(!open)}
            aria-expanded={open}
            area-haspopup={true}
          >
            <Icon icon="HamburgerMenu" width="18px" height="12px" />
          </OpenButton>
        )}
        <Container isOpen={open}>
          <RightAlign>
            {!alwaysOpen && (
              <CloseButton onClick={() => setOpen(!open)} aria-expanded={!open}>
                <Icon icon="ArrowLeftOpen" width="18px" />
              </CloseButton>
            )}
            <BottomBorder />
          </RightAlign>
          {props.children}
        </Container>
      </StyledSticky>
    </NavWrapper>
  );
};

Sidebar.propTypes = {
  /** Meant to be react router NavLinks */
  children: PropTypes.node,
  /** Optional boolean to indicate that the sidebar should always be open. Passing true here will also remove any expand and collapse functionality. This may go away in the near future.  */
  alwaysOpen: PropTypes.bool,
};

export default Sidebar;
